import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { QuotationSummaryService } from 'src/app/_Services/quotationNew/quotationsummary.service';
import { AttachmentFile } from 'src/app/_Models/fileupload/fileupload';
import { FileStoreService } from 'src/app/_Services/filestore/filestore.service';
import { InvoiceSummaryService } from 'src/app/_Services/invoice/invoice-summary.service';

@Component({
    selector: 'app-document-reupload-popup',
    templateUrl: './document-invoice-reupload-popup.component.html',
    styleUrls: ['./document-invoice-reupload-popup.component.scss']
})
export class DocumentInvoiceReuploadPopupComponent implements OnInit {
    @Input() invoiceId: number;
    @Input() invoiceData: any;
    @Input() fileData: any;
    @Input() existingSupportingDocuments: any[] = []; // Existing supporting documents from quotation

    // Supporting documents
    supportingDocuments: AttachmentFile[] = [];

    // Form data
    description: string = '';

    // Loading states
    isSubmitting: boolean = false;
    isSubmitted: boolean = false;
    isUploadingFiles: boolean = false;

    // Validation
    descriptionError: string = '';

    // File upload progress
    uploadProgress: number = 0;

    // Display existing files
    existingQuotationFile: any = null;
    existingSupportingDocs: any[] = [];

    constructor(
        public activeModal: NgbActiveModal,
        private toastr: ToastrService,
        private translate: TranslateService,
        private invoiceService: InvoiceSummaryService,
        private fileService: FileStoreService
    ) {
        console.log('Invoice ID:', this.invoiceId);
        console.log('Invoice Data:', this.invoiceData);
        console.log('File Data:', this.fileData);
    }

    ngOnInit(): void {
        console.log('Invoice ID:', this.invoiceId);
        console.log('Invoice Data:', this.invoiceData);
        console.log('File Data:', this.fileData);
        console.log('Existing Supporting Documents:', this.existingSupportingDocuments);

        // Set existing quotation file for display
        if (this.invoiceData && this.invoiceData.invoiceFileUrl) {
            this.existingQuotationFile = {
                fileName: this.invoiceData.invoiceFileName || 'Invoice.pdf',
                fileUrl: this.invoiceData.invoiceFileUrl
            };
        }

        // Set existing supporting documents for display
        if (this.existingSupportingDocuments && this.existingSupportingDocuments.length > 0) {
            this.existingSupportingDocs = this.existingSupportingDocuments.map(doc => ({
                fileName: doc.fileName || doc.name || 'Document',
                fileUrl: doc.fileUrl || doc.url,
                fileSize: doc.fileSize || 0
            }));
        }
    }

    // Handle supporting document upload
    onSupportingDocumentSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const maxFileSize = 10485760; // 10MB

        if (input.files && input.files.length > 0) {
            for (let i = 0; i < input.files.length; i++) {
                const file = input.files[i];

                // Validate file size
                if (file.size > maxFileSize) {
                    this.toastr.error(
                        this.translate.instant('INVOICE_SUMMARY.MSG_FILE_TOO_LARGE', { fileName: file.name })
                    );
                    continue;
                }

                // Check for duplicate files
                const isDuplicate = this.supportingDocuments.some(doc => doc.fileName === file.name);
                if (isDuplicate) {
                    this.toastr.warning(
                        this.translate.instant('INVOICE_SUMMARY.MSG_FILE_ALREADY_EXISTS', { fileName: file.name })
                    );
                    continue;
                }

                // Create attachment file object
                const attachmentFile = new AttachmentFile();
                attachmentFile.file = file;
                attachmentFile.fileName = file.name;
                attachmentFile.fileSize = file.size;
                attachmentFile.fileUrl = URL.createObjectURL(file);
                attachmentFile.uniqueld = this.generateUniqueId(file.name);

                this.supportingDocuments.push(attachmentFile);
            }

            // Reset input
            input.value = '';
        }
    }

    // Generate unique ID for file
    generateUniqueId(fileName: string): string {
        const dotIndex = fileName.lastIndexOf('.');
        const nameWithoutExt = dotIndex !== -1 ? fileName.substring(0, dotIndex) : fileName;
        const extension = dotIndex !== -1 ? fileName.substring(dotIndex + 1) : '';
        const timestamp = new Date().getTime();
        const randomStr = Math.random().toString(36).substring(2, 8);

        const cleanName = nameWithoutExt
            .trim()
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_');

        return `${cleanName}_${timestamp}_${randomStr}${extension ? '.' + extension : ''}`;
    }

    // Remove supporting document
    removeSupportingDocument(index: number) {
        if (this.supportingDocuments[index].fileUrl) {
            URL.revokeObjectURL(this.supportingDocuments[index].fileUrl);
        }
        this.supportingDocuments.splice(index, 1);
    }

    // Validate form
    validateForm(): boolean {
        let isValid = true;
        this.descriptionError = '';

        // if (!this.description || this.description.trim() === '') {
        //     this.descriptionError = this.translate.instant('INVOICE_SUMMARY.MSG_DESCRIPTION_REQUIRED');
        //     isValid = false;
        // }

        if (!this.fileData || !this.fileData.fileName) {
            this.toastr.error(this.translate.instant('INVOICE_SUMMARY.MSG_INVOICE_FILE_REQUIRED'));
            isValid = false;
        }

        if (!this.invoiceId) {
            this.toastr.error(this.translate.instant('INVOICE_SUMMARY.MSG_INVOICE_ID_REQUIRED'));
            isValid = false;
        }

        return isValid;
    }

    // Reupload quotation - Following the same pattern as submit quotation
    async reuploadInvoice() {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;
        this.isUploadingFiles = true;

        try {
            // Step 1: Upload main quotation file to cloud storage
            let quotationFileUrl = this.fileData.fileUrl || '';
            let quotationFileUniqueId = '';

            if (!quotationFileUrl && this.fileData && this.fileData.file) {

                const quotationFileAttachment = new AttachmentFile();
                quotationFileAttachment.file = this.fileData.file;
                quotationFileAttachment.fileName = this.fileData.fileName;
                quotationFileAttachment.uniqueld = this.generateUniqueId(this.fileData.fileName);

                const quotationUploadResponse = await this.fileService.uploadFileData(
                    18, // Quotation files container
                    [quotationFileAttachment]
                );

                if (quotationUploadResponse && quotationUploadResponse.fileUploadDataList && quotationUploadResponse.fileUploadDataList.length > 0) {
                    quotationFileUrl = quotationUploadResponse.fileUploadDataList[0].fileCloudUri;
                    quotationFileUniqueId = quotationUploadResponse.fileUploadDataList[0].fileName;
                } else {
                    throw new Error('Failed to upload quotation file');
                }
            }

            // Step 2: Upload supporting documents to cloud storage
            const supportingDocUrls: Array<{ fileName: string, fileUrl: string, uniqueId: string }> = [];

            if (this.supportingDocuments.length > 0) {

                const supportingDocsResponse = await this.fileService.uploadFileData(
                    18, // Quotation files container
                    this.supportingDocuments
                );

                if (supportingDocsResponse && supportingDocsResponse.fileUploadDataList) {
                    supportingDocsResponse.fileUploadDataList.forEach((uploadedFile, index) => {
                        supportingDocUrls.push({
                            fileName: uploadedFile.fileName,
                            fileUrl: uploadedFile.fileCloudUri,
                            uniqueId: uploadedFile.fileUniqueId
                        });
                    });
                }
            }

            this.isUploadingFiles = false;

            // Step 3: Prepare reupload data with cloud URLs
            const reuploadData = {
                invoiceId: this.invoiceId,
                invoiceFile: {
                    fileName: this.fileData.fileName,
                    fileUrl: this.fileData.fileUrl,
                    uniqueId: quotationFileUniqueId
                },
                supportingDocuments: supportingDocUrls,
                description: this.description
            };

            // Step 4: Submit reupload to backend with cloud URLs
            this.invoiceService.reuploadInvoice(reuploadData).subscribe(
                (response) => {
                    this.isSubmitting = false;
                    if (response && response.result === 1) {
                        this.isSubmitted = true;
                        // this.toastr.success(this.translate.instant('INVOICE_SUMMARY.REUPLOAD_SUCCESS_MESSAGE'));

                        // Close modal and pass success data to parent
                        // setTimeout(() => {
                        //     this.activeModal.close({ success: true, data: response });

                        // }, 1000);
                    } else {
                        this.toastr.error(response.message || this.translate.instant('INVOICE_SUMMARY.MSG_REUPLOAD_ERROR'));
                    }
                },
                (error) => {
                    this.isSubmitting = false;
                    this.toastr.error(this.translate.instant('INVOICE_SUMMARY.MSG_REUPLOAD_ERROR'));
                    console.error('Reupload error:', error);
                }
            );

        } catch (error) {
            this.isSubmitting = false;
            this.isUploadingFiles = false;
            this.toastr.error(this.translate.instant('INVOICE_SUMMARY.MSG_REUPLOAD_ERROR'));
            console.error('File upload error:', error);
        }
    }

    // Cancel and close modal
    cancel() {
        // Clean up object URLs
        this.supportingDocuments.forEach(doc => {
            if (doc.fileUrl) {
                URL.revokeObjectURL(doc.fileUrl);
            }
        });

        this.activeModal.dismiss();
    }

    // Get file size in readable format
    getFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

      getImageIcon(file) {

    if (file.fileName) {
      var splitFiles = file.fileName.split('.');
      var fileExtension = splitFiles[splitFiles.length - 1].toLocaleLowerCase();

      if (fileExtension == "pdf")
        return "assets/images/new-set/pdf-icon.svg";
      else if (fileExtension == "doc" || fileExtension == "docx")
        return "assets/images/new-set/word-icon.svg";
      else if (fileExtension == "png" || fileExtension == "jpg" || fileExtension == "jpeg")
        return "assets/images/new-set/image-icon.svg";
      else if (fileExtension == "xlsx" || fileExtension == "xls")
        return "assets/images/new-set/excel-icon.svg";

    }
  }
     
  closeSuccessPopup() {
    this.isSubmitted = false;
    this.activeModal.close({ success: true  , isEmpty: true});
  }
}
